/**
 * PostHog Tracking Utilities for Kursivia Tenant App
 * 
 * This module provides:
 * - Identity management (Auth0 integration)
 * - Tenant context for all events
 * - Custom event tracking helpers
 * - GDPR-compliant tracking (no PII in unauthenticated events)
 */

import posthog from 'posthog-js';

// Event names (snake_case as per spec)
export const EVENTS = {
  // Page views (automatic via PostHog)
  PAGE_VIEW: '$pageview',
  
  // Course events
  COURSE_VIEWED: 'course_viewed',
  COURSE_BOOKED: 'course_booked',
  BOOKING_CANCELLED: 'booking_cancelled',
  
  // Session events
  SESSION_ATTENDANCE_MARKED: 'session_attendance_marked',
  SESSION_CANCELLED: 'session_cancelled',
  
  // Gym events
  GYM_SLOT_BOOKED: 'gym_slot_booked',
  GYM_BOOKING_CANCELLED: 'gym_booking_cancelled',
  GYM_CHECK_IN: 'gym_check_in',
  
  // Auth events
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  
  // Admin events
  COURSE_CREATED: 'course_created',
  COURSE_UPDATED: 'course_updated',
  COURSE_PUBLISHED: 'course_published',
  INSTRUCTOR_CREATED: 'instructor_created',
  USER_INVITED: 'user_invited',
  
  // Block course events
  BLOCK_CREATED: 'block_created',
  BLOCK_BOOKED: 'block_booked',
} as const;

// Tenant context interface
interface TenantContext {
  tenant_slug: string;
  tenant_id?: string;
  source_domain: 'tenant_app';
}

// User context interface
interface UserContext {
  user_role?: string;
  is_owner?: boolean;
}

// Store for tenant context
let tenantContext: TenantContext | null = null;

// Store for user context
let userContext: UserContext | null = null;

// Flag to track if user has been identified
let isIdentified = false;

/**
 * Initialize tenant context for all events
 * Call this once when the app loads with tenant config
 */
export function initTenantContext(tenantSlug: string, tenantId?: string): void {
  tenantContext = {
    tenant_slug: tenantSlug,
    tenant_id: tenantId,
    source_domain: 'tenant_app',
  };
  
  // Register as super properties so they're included in all events
  if (posthog.__loaded) {
    posthog.register(tenantContext);
  }
}

/**
 * Identify user with Auth0 sub (distinct_id)
 * Call this after successful authentication
 * 
 * @param auth0Sub - The Auth0 user ID (sub claim)
 * @param email - User's email address
 * @param userRole - User's role (user, instructor, courseadmin, admin)
 * @param isOwner - Whether user is the tenant owner
 */
export function identifyUser(
  auth0Sub: string,
  email: string,
  userRole?: string,
  isOwner?: boolean
): void {
  if (!posthog.__loaded) {
    return;
  }
  
  // Identify with person properties
  const personProperties: Record<string, any> = {
    email,
    ...(tenantContext || {}),
  };
  
  if (userRole) {
    personProperties.user_role = userRole;
  }
  
  if (isOwner !== undefined) {
    personProperties.is_owner = isOwner;
  }
  
  posthog.identify(auth0Sub, personProperties);
  
  // Store user context for event properties
  userContext = {
    user_role: userRole,
    is_owner: isOwner,
  };
  
  isIdentified = true;
}

/**
 * Reset user identity on logout
 */
export function resetIdentity(): void {
  if (posthog.__loaded) {
    posthog.reset();
  }
  userContext = null;
  isIdentified = false;
}

/**
 * Track a custom event with automatic context
 * 
 * @param eventName - Event name from EVENTS constant
 * @param properties - Additional event properties
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  if (!posthog.__loaded) {
    return;
  }
  
  // Build event properties
  const eventProperties: Record<string, any> = {
    ...properties,
    // Always include tenant context
    ...(tenantContext || {}),
  };
  
  // Include user context if identified
  if (isIdentified && userContext) {
    eventProperties.user_role = userContext.user_role;
    eventProperties.is_owner = userContext.is_owner;
  }
  
  posthog.capture(eventName, eventProperties);
}

// ============================================
// Convenience tracking functions
// ============================================

/**
 * Track course viewed event
 */
export function trackCourseViewed(courseId: number, courseName: string, instructorName?: string): void {
  trackEvent(EVENTS.COURSE_VIEWED, {
    course_id: courseId,
    course_name: courseName,
    instructor_name: instructorName,
  });
}

/**
 * Track course booked event
 */
export function trackCourseBooked(
  courseId: number,
  courseName: string,
  bookingId: number,
  isWaitlist: boolean = false,
  blockId?: number
): void {
  trackEvent(EVENTS.COURSE_BOOKED, {
    course_id: courseId,
    course_name: courseName,
    booking_id: bookingId,
    is_waitlist: isWaitlist,
    block_id: blockId,
  });
}

/**
 * Track booking cancelled event
 */
export function trackBookingCancelled(bookingId: number, courseId: number, courseName: string): void {
  trackEvent(EVENTS.BOOKING_CANCELLED, {
    booking_id: bookingId,
    course_id: courseId,
    course_name: courseName,
  });
}

/**
 * Track session attendance marked event
 */
export function trackSessionAttendanceMarked(
  sessionId: number,
  courseId: number,
  attendedCount: number,
  totalCount: number
): void {
  trackEvent(EVENTS.SESSION_ATTENDANCE_MARKED, {
    session_id: sessionId,
    course_id: courseId,
    attended_count: attendedCount,
    total_count: totalCount,
    attendance_rate: totalCount > 0 ? (attendedCount / totalCount) * 100 : 0,
  });
}

/**
 * Track user login event
 */
export function trackUserLogin(method: string = 'auth0'): void {
  trackEvent(EVENTS.USER_LOGIN, {
    method,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track user logout event
 */
export function trackUserLogout(): void {
  trackEvent(EVENTS.USER_LOGOUT, {
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track course created event (admin)
 */
export function trackCourseCreated(courseId: number, courseName: string, isBlockCourse: boolean = false): void {
  trackEvent(EVENTS.COURSE_CREATED, {
    course_id: courseId,
    course_name: courseName,
    is_block_course: isBlockCourse,
  });
}

/**
 * Track course published event (activation indicator)
 * Fires when a course becomes bookable (isActive: true)
 */
export function trackCoursePublished(courseId: number, courseName: string, isBlockCourse: boolean = false): void {
  trackEvent(EVENTS.COURSE_PUBLISHED, {
    course_id: courseId,
    course_name: courseName,
    is_block_course: isBlockCourse,
  });
}

/**
 * Track gym slot booked event
 */
export function trackGymSlotBooked(slotId: number, serviceName: string): void {
  trackEvent(EVENTS.GYM_SLOT_BOOKED, {
    slot_id: slotId,
    service_name: serviceName,
  });
}

/**
 * Track gym check-in event
 */
export function trackGymCheckIn(bookingId: number, slotId: number): void {
  trackEvent(EVENTS.GYM_CHECK_IN, {
    booking_id: bookingId,
    slot_id: slotId,
  });
}

/**
 * Track block created event (admin)
 */
export function trackBlockCreated(blockId: number, courseId: number, courseName: string): void {
  trackEvent(EVENTS.BLOCK_CREATED, {
    block_id: blockId,
    course_id: courseId,
    course_name: courseName,
  });
}

/**
 * Track block booked event
 */
export function trackBlockBooked(blockId: number, courseId: number, courseName: string, bookingId: number): void {
  trackEvent(EVENTS.BLOCK_BOOKED, {
    block_id: blockId,
    course_id: courseId,
    course_name: courseName,
    booking_id: bookingId,
  });
}

// ============================================
// React Hook
// ============================================

/**
 * Check if PostHog is initialized and ready
 */
export function isTrackingReady(): boolean {
  return posthog.__loaded === true;
}

/**
 * Get the current distinct ID
 */
export function getDistinctId(): string | undefined {
  if (posthog.__loaded) {
    return posthog.get_distinct_id();
  }
  return undefined;
}
